/**
 * Attività: le frasi del registro e i testi della pagina.
 *
 * Schema delle chiavi: una frase è `attivita.<azione>` (l'azione ha sempre un
 * punto, come `stack.deploy`); i testi della pagina hanno un solo segmento
 * (`attivita.titolo`) e le etichette del filtro stanno sotto `attivita.filtro.`.
 * `AZIONI_NOTE` ricava l'elenco dalle sole frasi, quindi nuovi testi di pagina
 * non vengono scambiati per azioni.
 *
 * Nelle frasi dello stack, `stack` è un segnaposto che l'interfaccia sostituisce
 * con un link.
 */
export const attivita = {
  "attivita.titolo": "Attività",
  "attivita.intro": "Chi ha fatto cosa su Portman, anche con i webhook. Le voci si aggiungono soltanto: non si possono modificare.",
  "attivita.vuoto": "Nessuna attività",
  "attivita.intervallo": (p: { da: number; a: number; totale: number }) => `${p.da}–${p.a} di ${p.totale}`,
  "attivita.precedenti": "Precedenti",
  "attivita.successive": "Successive",
  "attivita.tutteAzioni": "Tutte le azioni",
  "attivita.tuttiStack": "Tutti gli stack",
  "attivita.tuttiUtenti": "Tutti gli utenti",
  "attivita.filtroAzione": "Filtra per azione",
  "attivita.filtroStack": "Filtra per stack",
  "attivita.filtroUtente": "Filtra per utente",
  "attivita.dettagli": "dettagli",
  "attivita.ip": "Indirizzo IP di provenienza",
  "attivita.fallita": "non riuscita",
  "attivita.auth.login": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha effettuato l'accesso`,
  "attivita.auth.logout": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} è uscito`,
  "attivita.auth.password_change": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha cambiato la propria password`,
  "attivita.backup.export": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha scaricato una copia del database`,
  "attivita.backup.restore": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha ripristinato il database da una copia`,
  "attivita.cartella.create": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha aggiunto una cartella dell'host`,
  "attivita.cartella.delete": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha tolto una cartella dell'host`,
  "attivita.notifiche.save": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha cambiato email e avvisi`,
  "attivita.notifiche.test": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha inviato un'email di prova`,
  "attivita.registry.save": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha salvato un registry privato`,
  "attivita.registry.remove": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha rimosso un registry privato`,
  "attivita.stack.create": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha creato ${p.stack}`,
  "attivita.stack.duplicate": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha duplicato ${p.stack}`,
  "attivita.stack.save": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha salvato una nuova revisione di ${p.stack}`,
  "attivita.stack.update": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha modificato ${p.stack}`,
  "attivita.stack.rename": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha rinominato ${p.stack}`,
  "attivita.stack.delete": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha eliminato ${p.stack}`,
  "attivita.stack.import": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha importato ${p.stack}`,
  "attivita.stack.avvisi.save": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha cambiato gli avvisi di ${p.stack}`,
  "attivita.stack.deploy": (p: { attore: string; stack: string; revisione?: number }) =>
    `${p.attore} ha rilasciato ${p.stack}${p.revisione === undefined ? "" : ` rev. ${p.revisione}`}`,
  "attivita.stack.stop": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha fermato ${p.stack}`,
  "attivita.stack.restart": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha riavviato ${p.stack}`,
  "attivita.token.create": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha creato un token di lettura`,
  "attivita.token.revoke": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha revocato un token di lettura`,
  "attivita.user.bootstrap": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha creato il primo account`,
  "attivita.user.create": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha creato un utente`,
  "attivita.user.update": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha modificato un utente`,
  "attivita.user.delete": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha eliminato un utente`,
  "attivita.webhook.create": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha creato un webhook`,
  "attivita.webhook.revoke": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha revocato un webhook`,
  "attivita.impostazioni.lingua": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha cambiato la lingua dell'istanza`,
  "attivita.filtro.auth.login": "Accessi",
  "attivita.filtro.auth.logout": "Uscite",
  "attivita.filtro.auth.password_change": "Cambi di password",
  "attivita.filtro.backup.export": "Copie scaricate",
  "attivita.filtro.backup.restore": "Ripristini",
  "attivita.filtro.cartella.create": "Cartelle aggiunte",
  "attivita.filtro.cartella.delete": "Cartelle tolte",
  "attivita.filtro.notifiche.save": "Email e avvisi",
  "attivita.filtro.notifiche.test": "Email di prova",
  "attivita.filtro.registry.save": "Registry salvati",
  "attivita.filtro.registry.remove": "Registry rimossi",
  "attivita.filtro.stack.create": "Stack creati",
  "attivita.filtro.stack.duplicate": "Stack duplicati",
  "attivita.filtro.stack.save": "Revisioni salvate",
  "attivita.filtro.stack.update": "Stack modificati",
  "attivita.filtro.stack.rename": "Stack rinominati",
  "attivita.filtro.stack.delete": "Stack eliminati",
  "attivita.filtro.stack.import": "Stack importati",
  "attivita.filtro.stack.avvisi.save": "Avvisi cambiati",
  "attivita.filtro.stack.deploy": "Rilasci",
  "attivita.filtro.stack.stop": "Arresti",
  "attivita.filtro.stack.restart": "Riavvii",
  "attivita.filtro.token.create": "Token creati",
  "attivita.filtro.token.revoke": "Token revocati",
  "attivita.filtro.user.bootstrap": "Primo account",
  "attivita.filtro.user.create": "Utenti creati",
  "attivita.filtro.user.update": "Utenti modificati",
  "attivita.filtro.user.delete": "Utenti eliminati",
  "attivita.filtro.webhook.create": "Webhook creati",
  "attivita.filtro.webhook.revoke": "Webhook revocati",
  "attivita.filtro.impostazioni.lingua": "Cambi di lingua",
  "attivita.ip.block": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha bloccato un indirizzo IP`,
  "attivita.ip.unblock": (p: { attore: string; stack: string; revisione?: number }) => `${p.attore} ha sbloccato un indirizzo IP`,
  "attivita.filtro.ip.block": "IP bloccati",
  "attivita.filtro.ip.unblock": "IP sbloccati",
};
